import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Excel-style cell selection + live aggregate for the statement grids.
//
// Entirely client-side over rows already materialised in the browser: selecting cells
// never calls the API, never re-finalizes, and never touches the reconciliation cache,
// so it adds exactly zero to recon wall-clock (CLAUDE.md §11). The aggregate is
// computed over the SELECTED cells only — never a rescan of the frame — so cost is
// O(selected) per pointer move, not O(rows).
//
// Two selection modes finance actually uses:
//   • rectangle — drag (or shift-click) a block of cells, like Excel
//   • whole column — click a header; ctrl/cmd-click adds more columns
// A column selection deliberately spans every row passing the ACTIVE header filters,
// not just the visible page: "what do the unmatched premiums foot to?" must not
// silently answer for page 1 of 4.

export interface CellAnchor {
  row: number;  // index into the FILTERED row list (not the raw frame)
  col: number;  // index into the visible column list
}

export interface Aggregate {
  count: number;   // numeric cells only
  sum: number;
  avg: number;
  min: number;
  max: number;
  nonNumeric: number;  // selected cells that held text — surfaced, never silently dropped
}

export type SelectionMode = "rect" | "columns";

export interface Selection {
  mode: SelectionMode;
  anchor: CellAnchor;
  focus: CellAnchor;
  cols: number[];  // populated in "columns" mode
}

// Parse a cell to a number for aggregation. The frames carry real JS numbers for
// amount columns (the API serialises them as numbers), so the fast path is a typeof
// check. Strings are accepted only when they are unambiguously numeric after
// stripping thousands separators — a policy number like "1,204" would parse, so this
// is applied ONLY to cells the grid already right-aligns as numeric (see isNumericCell).
export function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const s = v.trim().replace(/,/g, "");
    if (s === "") return null;
    // Accept a leading sign, digits, one decimal point, and Excel-style (1.23) negatives.
    if (!/^-?\(?-?\d*\.?\d+\)?$/.test(s)) return null;
    const neg = s.startsWith("(") && s.endsWith(")");
    const n = Number(neg ? s.slice(1, -1) : s);
    if (!Number.isFinite(n)) return null;
    return neg ? -n : n;
  }
  return null;
}

// Only true numbers count as numeric cells. Mirrors the grid's own `typeof v ===
// "number"` right-alignment rule so what the user sees right-aligned is exactly what
// the status bar aggregates — no surprise inclusions from numeric-looking text.
export function isNumericCell(v: unknown): boolean {
  return typeof v === "number" && Number.isFinite(v);
}

// A cell the grid renders as an em-dash, i.e. one the user SEES as empty. Must track
// columns.ts::fmtCell: pandas leaks the strings "nan"/"NaT"/"None" into the payload and
// fmtCell blanks them, so counting them as "text cells ignored" reported ignored
// content in a selection that visibly holds none. Non-finite numbers (NaN/Infinity)
// are blank here too — isNumericCell rejects them, and without this they would fall
// through to the nonNumeric branch.
function isBlankValue(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === "number") return !Number.isFinite(v);
  if (typeof v !== "string") return false;
  const s = v.trim();
  return s === "" || s === "nan" || s === "NaT" || s === "None";
}

function normalize(sel: Selection): { r0: number; r1: number; c0: number; c1: number } {
  const r0 = Math.min(sel.anchor.row, sel.focus.row);
  const r1 = Math.max(sel.anchor.row, sel.focus.row);
  const c0 = Math.min(sel.anchor.col, sel.focus.col);
  const c1 = Math.max(sel.anchor.col, sel.focus.col);
  return { r0, r1, c0, c1 };
}

export interface UseCellSelection {
  selection: Selection | null;
  aggregate: Aggregate | null;
  /** True if the cell at (filtered row, visible col) is inside the selection. */
  isSelected: (row: number, col: number) => boolean;
  /** True if a whole column is selected (drives the header highlight). */
  isColSelected: (col: number) => boolean;
  clear: () => void;
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent) => void;
  onCellMouseEnter: (row: number, col: number) => void;
  onHeaderClick: (col: number, e: React.MouseEvent) => void;
  /** Selected cells as TSV, for clipboard copy. Null when nothing is selected. */
  toTsv: () => string | null;
  /** Attach to the grid's scrolling container to enable drag-past-the-edge autoscroll. */
  scrollHostRef: React.MutableRefObject<HTMLDivElement | null>;
}

// Autoscroll tuning. EDGE is the hot zone inside each border; speed ramps linearly with
// how far past the edge the pointer is, so a small overshoot creeps and a large one
// races — the standard Excel/Sheets feel.
const EDGE_PX = 48;
const MAX_SPEED_PX = 22;   // per frame at full penetration (~1300px/s at 60fps)

/**
 * @param rows    the FILTERED rows currently backing the grid
 * @param colIdx  visible-column position → index into a row array
 * @param labels  visible-column headers, used for the TSV copy header line
 * @param rowIds  stable per-row identity, parallel to `rows` (the frame's original
 *                pandas index). Drives the stale-selection guard; see rowsKey.
 */
export function useCellSelection(
  rows: unknown[][],
  colIdx: number[],
  labels: string[],
  rowIds?: (string | number)[],
): UseCellSelection {
  const [selection, setSelection] = useState<Selection | null>(null);
  const dragging = useRef(false);
  // Autoscroll state (see the rAF loop below). Declared here so the mouseup handler can
  // cancel an in-flight frame.
  const scrollHostRef = useRef<HTMLDivElement | null>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const rafId = useRef<number | null>(null);

  // Any change to the underlying row SET (new frame, filter change, re-finalize) makes
  // row positions meaningless — a stale selection would aggregate the wrong cells.
  //
  // Keyed on CONTENT, deliberately not on the `rows` array identity: the grid rebuilds
  // `filtered` on every render (and DataTable's own setPage(0) effect re-renders
  // whenever its filter object is recreated), so an identity dep cleared the selection
  // on the render right after it was made — the status bar never appeared while a
  // search was active.
  //
  // The key MUST distinguish any two row sets a filter can produce, or a selection
  // survives onto different rows and foots the wrong number. `rowIds` (the frame's
  // original pandas index, unique per row) is what makes that true: an earlier version
  // keyed on count + first/last row COLUMN 0, which collides whenever column 0 is a
  // categorical — filtering [POL-1,POL-2,POL-3] down to [POL-1,POL-3,POL-4] keeps
  // count=3 and the same first/last col-0 value, so the stale selection silently
  // re-aggregated and reported a total for rows no longer on screen.
  //
  // Every id is folded in, not a sample: sampling leaves a window (on a 69k-row frame,
  // any equal-size swap confined between two probes) where a changed row set still
  // looks identical, and this figure must never be wrong. FNV-1a over the ids costs
  // ~0.5ms at 69k rows against a ~5ms filter pass — measured, not assumed (§11).
  const rowsKey = useMemo(() => {
    const n = rows.length;
    if (!n) return "0";
    const ids = rowIds && rowIds.length === n ? rowIds : null;
    let h = 0x811c9dc5 >>> 0;
    const step = (x: number) => { h ^= x; h = Math.imul(h, 0x01000193) >>> 0; };
    for (let i = 0; i < n; i++) {
      const id = ids ? ids[i] : rows[i]?.[0];
      // Small non-negative integers (the normal case — pandas row indices) hash
      // without allocating a string per row; that alone is the difference between
      // ~1ms and ~7ms at 69k. Anything else (huge, fractional, negative, non-numeric)
      // falls back to the string path so no two distinct ids can collapse together.
      if (typeof id === "number" && Number.isInteger(id) && id >= 0 && id <= 0x7fffffff) step(id);
      else { const s = String(id ?? ""); for (let k = 0; k < s.length; k++) step(s.charCodeAt(k)); }
      step(0x2c);   // delimiter: ids "1","23" must not hash the same as "12","3"
    }
    return `${n}|${h}`;
  }, [rows, rowIds]);
  useEffect(() => { setSelection(null); }, [rowsKey]);

  // A drag that ends outside the table still has to stop tracking. Bound on the window
  // (capture) so a mouseup over the scrollbar, the status bar, or outside the document
  // still terminates the gesture — otherwise the grid would keep "painting" a selection
  // after the button was released.
  useEffect(() => {
    const up = () => {
      dragging.current = false;
      if (rafId.current !== null) { cancelAnimationFrame(rafId.current); rafId.current = null; }
    };
    window.addEventListener("mouseup", up, true);
    window.addEventListener("blur", up);
    return () => {
      window.removeEventListener("mouseup", up, true);
      window.removeEventListener("blur", up);
    };
  }, []);

  // Drag-past-the-edge autoscroll.
  //
  // Driven by a rAF loop rather than by mousemove, because mousemove stops firing the
  // moment the pointer stops — holding still just past the edge (exactly what a user
  // does when reaching for an off-screen column) would otherwise scroll one step and
  // freeze. The loop is started only on entering an edge zone and cancelled the instant
  // the pointer leaves it or the button is released, so a drag that never approaches an
  // edge costs literally nothing: no rAF is ever scheduled (§11).
  //
  // While the pointer is stationary and content slides underneath it, `mouseenter` does
  // not reliably fire, so each tick re-resolves the cell under the pointer via
  // elementFromPoint and extends the focus itself. That read is one hit-test per frame
  // against cells already laid out — it does not touch the row data.
  const stopAutoScroll = useCallback(() => {
    if (rafId.current !== null) { cancelAnimationFrame(rafId.current); rafId.current = null; }
  }, []);

  // Resolve the cell under the pointer and extend the rectangle to it. Cells carry their
  // coordinates as data attributes so this stays a DOM read with no row-index math.
  const extendToPointer = useCallback(() => {
    const el = document.elementFromPoint(pointer.current.x, pointer.current.y);
    const td = el && (el as HTMLElement).closest?.("td[data-row]");
    if (!td) return;
    const row = Number((td as HTMLElement).dataset.row);
    const col = Number((td as HTMLElement).dataset.col);
    if (!Number.isFinite(row) || !Number.isFinite(col)) return;
    setSelection((prev) =>
      prev && prev.mode === "rect" && (prev.focus.row !== row || prev.focus.col !== col)
        ? { ...prev, focus: { row, col } }
        : prev,   // identity preserved when unchanged → no re-render, no re-aggregate
    );
  }, []);

  const tick = useCallback(() => {
    const host = scrollHostRef.current;
    if (!dragging.current || !host) { rafId.current = null; return; }
    const r = host.getBoundingClientRect();
    const { x, y } = pointer.current;

    // Penetration depth past each edge, clamped to the hot zone, → 0..1 speed ramp.
    const ramp = (depth: number) => Math.min(depth, EDGE_PX) / EDGE_PX * MAX_SPEED_PX;
    let dx = 0, dy = 0;
    if (x > r.right - EDGE_PX) dx = ramp(x - (r.right - EDGE_PX));
    else if (x < r.left + EDGE_PX) dx = -ramp((r.left + EDGE_PX) - x);
    if (y > r.bottom - EDGE_PX) dy = ramp(y - (r.bottom - EDGE_PX));
    else if (y < r.top + EDGE_PX) dy = -ramp((r.top + EDGE_PX) - y);

    if (dx === 0 && dy === 0) { rafId.current = null; return; }  // left the edge → stop

    const beforeL = host.scrollLeft, beforeT = host.scrollTop;
    if (dx) host.scrollLeft += dx;
    if (dy) host.scrollTop += dy;
    // Hit the end of the scrollable range in both axes → nothing more to reveal.
    if (host.scrollLeft === beforeL && host.scrollTop === beforeT) { rafId.current = null; return; }

    extendToPointer();
    rafId.current = requestAnimationFrame(tick);
  }, [extendToPointer]);

  const maybeAutoScroll = useCallback((clientX: number, clientY: number) => {
    pointer.current = { x: clientX, y: clientY };
    if (!dragging.current || rafId.current !== null) return;
    const host = scrollHostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();
    const inEdge =
      clientX > r.right - EDGE_PX || clientX < r.left + EDGE_PX ||
      clientY > r.bottom - EDGE_PX || clientY < r.top + EDGE_PX;
    if (inEdge) rafId.current = requestAnimationFrame(tick);
  }, [tick]);

  // One window-level mousemove while dragging keeps `pointer` fresh and (re)arms the
  // loop. Registered unconditionally but it early-outs on the `dragging` ref, so the
  // non-dragging case is a single boolean test.
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current) return;
      maybeAutoScroll(e.clientX, e.clientY);
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, [maybeAutoScroll]);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const onCellMouseDown = useCallback((row: number, col: number, e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();  // suppress native text selection while dragging cells
    dragging.current = true;
    pointer.current = { x: e.clientX, y: e.clientY };
    setSelection((prev) =>
      e.shiftKey && prev && prev.mode === "rect"
        ? { ...prev, focus: { row, col } }
        : { mode: "rect", anchor: { row, col }, focus: { row, col }, cols: [] },
    );
  }, []);

  const onCellMouseEnter = useCallback((row: number, col: number) => {
    if (!dragging.current) return;
    setSelection((prev) => (prev && prev.mode === "rect" ? { ...prev, focus: { row, col } } : prev));
  }, []);

  const onHeaderClick = useCallback((col: number, e: React.MouseEvent) => {
    const additive = e.ctrlKey || e.metaKey;
    setSelection((prev) => {
      const base = prev && prev.mode === "columns" && additive ? prev.cols : [];
      const cols = base.includes(col) ? base.filter((c) => c !== col) : [...base, col];
      if (!cols.length) return null;
      return { mode: "columns", anchor: { row: 0, col }, focus: { row: 0, col }, cols };
    });
  }, []);

  const clear = useCallback(() => setSelection(null), []);

  const isSelected = useCallback((row: number, col: number) => {
    if (!selection) return false;
    if (selection.mode === "columns") return selection.cols.includes(col);
    const { r0, r1, c0, c1 } = normalize(selection);
    return row >= r0 && row <= r1 && col >= c0 && col <= c1;
  }, [selection]);

  const isColSelected = useCallback(
    (col: number) => !!selection && selection.mode === "columns" && selection.cols.includes(col),
    [selection],
  );

  // Walk exactly the selected cells, ROW-MAJOR in both modes: the outer loop strides
  // rows and the inner loop hits the few selected columns within a row that is already
  // in cache. The obvious column-major order (for each col, scan every row) touches one
  // element per row array and thrashes cache — measured on a 96k-row frame it costs
  // 105ms for 31 columns vs 18ms row-major (5.7x), which is a visible hitch; the two are
  // equal for the common single-column case. Memoised on `selection`, so it runs once
  // per selection change, not per render.
  const aggregate = useMemo<Aggregate | null>(() => {
    if (!selection) return null;
    let count = 0, sum = 0, comp = 0, nonNumeric = 0;
    let min = Infinity, max = -Infinity;

    // Hoisted out of the loop: `v === ""` is not "non-numeric", it's an empty cell.
    //
    // Neumaier compensated summation, not `sum += n`. Naive accumulation loses the low
    // bits whenever a running total dwarfs the next addend — footing a large opening
    // balance against many small movements (routine here) drifts into the third decimal,
    // and a figure finance ties to the cent must not depend on row order. `comp` carries
    // the lost remainder and is folded back once at the end.
    const visitValue = (v: unknown) => {
      if (isBlankValue(v)) return;  // blanks are not "non-numeric"
      if (isNumericCell(v)) {
        const n = v as number;
        count += 1;
        const t = sum + n;
        comp += Math.abs(sum) >= Math.abs(n) ? (sum - t) + n : (n - t) + sum;
        sum = t;
        if (n < min) min = n;
        if (n > max) max = n;
      } else {
        nonNumeric += 1;
      }
    };

    if (selection.mode === "columns") {
      // Resolve the frame column indices once, then one pass over the rows.
      const idx: number[] = [];
      for (const c of selection.cols) {
        const ci = colIdx[c];
        if (ci !== undefined) idx.push(ci);
      }
      for (let r = 0; r < rows.length; r++) {
        const row = rows[r];
        if (!row) continue;
        for (let k = 0; k < idx.length; k++) visitValue(row[idx[k]]);
      }
    } else {
      const visit = (r: number, c: number) => {
        const ci = colIdx[c];
        if (ci === undefined) return;
        visitValue(rows[r]?.[ci]);
      };
      const { r0, r1, c0, c1 } = normalize(selection);
      for (let r = r0; r <= r1 && r < rows.length; r++) for (let c = c0; c <= c1; c++) visit(r, c);
    }

    if (!count && !nonNumeric) return null;
    const total = sum + comp;   // fold the compensated remainder back in, once
    return {
      count, sum: total,
      avg: count ? total / count : 0,
      min: count ? min : 0,
      max: count ? max : 0,
      nonNumeric,
    };
  }, [selection, rows, colIdx]);

  // TSV so a paste into Excel lands as a proper grid. Values are emitted RAW (full
  // precision, unformatted) — a pasted 1204.5 must not become the display string
  // "1,204.50" and stop being a number in the user's workbook.
  const toTsv = useCallback((): string | null => {
    if (!selection) return null;
    const cell = (r: number, c: number): string => {
      const ci = colIdx[c];
      const v = ci === undefined ? null : rows[r]?.[ci];
      if (v === null || v === undefined) return "";
      return String(v).replace(/[\t\r\n]/g, " ");
    };
    const lines: string[] = [];
    if (selection.mode === "columns") {
      const cols = selection.cols;
      lines.push(cols.map((c) => labels[c] ?? "").join("\t"));
      for (let r = 0; r < rows.length; r++) lines.push(cols.map((c) => cell(r, c)).join("\t"));
    } else {
      const { r0, r1, c0, c1 } = normalize(selection);
      for (let r = r0; r <= r1 && r < rows.length; r++) {
        const out: string[] = [];
        for (let c = c0; c <= c1; c++) out.push(cell(r, c));
        lines.push(out.join("\t"));
      }
    }
    return lines.join("\n");
  }, [selection, rows, colIdx, labels]);

  return {
    selection, aggregate, isSelected, isColSelected, clear,
    onCellMouseDown, onCellMouseEnter, onHeaderClick, toTsv, scrollHostRef,
  };
}
